import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { TableColumnsType } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../api/errors';
import { createRole, getRole, listRoles, setRoleActive, updateRole } from '../api/roles';
import type { RoleDetail, RoleSummary } from '../api/roles';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { RoleDataScopeModal } from './roles/RoleDataScopeModal';
import { RolePermissionsModal } from './roles/RolePermissionsModal';

/** Giá trị của biểu mẫu lập vai trò mới. */
interface CreateFormValues {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
}

/** Giá trị của biểu mẫu sửa vai trò. */
interface EditFormValues {
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
  readonly isAssignable: boolean;
  readonly maximumApprovalAmount: number | null;
}

/** Dữ liệu của một trang đã nạp xong, kèm khóa nhận dạng lần nạp đó. */
interface LoadedPage {
  readonly key: string;
  readonly items: readonly RoleSummary[];
  readonly totalCount: number;
}

/** Số bản ghi mỗi trang. Khớp với mặc định của máy chủ. */
const pageSize = 25;

/**
 * Trang quản trị vai trò.
 *
 * **Đây là màn hình cuối để hệ thống tự vận hành không cần chạm cơ sở dữ liệu.** Trước nó, việc lập
 * một vai trò nghiệp vụ — ví dụ vai trò kinh doanh chỉ thấy khách hàng mình phụ trách — phải khai bằng
 * câu lệnh SQL viết tay.
 *
 * Bảng hiện **ba con số** ở mỗi dòng, và con số thứ ba là điểm quan trọng nhất của cả trang: số quy
 * tắc phạm vi dữ liệu. Vai trò không có quy tắc phạm vi nào thì người giữ nó **không thấy dòng dữ liệu
 * nào** dù đã được cấp đủ quyền — họ mở màn hình ra và thấy danh sách rỗng. Đó là cấu hình sai dễ mắc
 * nhất khi lập vai trò mới, nên nó được tô cảnh báo ngay ở danh sách thay vì phải mở từng vai trò ra
 * xem.
 */
export function RolePage(): ReactNode {
  const { hasPermission } = useAuth();

  const canManageRole = hasPermission(PermissionCodes.RoleManage);
  const canViewAssignment = hasPermission(PermissionCodes.PermissionAssignmentView);
  const canManageAssignment = hasPermission(PermissionCodes.PermissionAssignmentManage);

  const [createForm] = Form.useForm<CreateFormValues>();
  const [editForm] = Form.useForm<EditFormValues>();

  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [loaded, setLoaded] = useState<LoadedPage | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleSummary | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const [permissionsRole, setPermissionsRole] = useState<RoleDetail | null>(null);
  const [dataScopeRole, setDataScopeRole] = useState<RoleDetail | null>(null);

  const requestKey = useMemo(
    () => JSON.stringify({ searchTerm, includeInactive, pageNumber, reloadToken }),
    [searchTerm, includeInactive, pageNumber, reloadToken],
  );

  const isLoading = loaded?.key !== requestKey && errorMessage === null;

  const reload = useCallback((): void => {
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const page = await listRoles({ searchTerm, includeInactive, pageNumber, pageSize });

        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(null);
        setLoaded({ key: requestKey, items: page.items, totalCount: page.totalCount });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError
            ? error.message
            : 'Không kết nối được tới máy chủ. Kiểm tra lại đường mạng.',
        );
      }
    })();

    return () => {
      controller.abort();
    };
  }, [requestKey, searchTerm, includeInactive, pageNumber]);

  const items = useMemo<readonly RoleSummary[]>(() => loaded?.items ?? [], [loaded]);
  const totalCount = loaded?.totalCount ?? 0;

  function openCreateForm(): void {
    createForm.setFieldsValue({
      code: '',
      name: '',
      nameEn: null,
      description: null,
      sortOrder: 500,
    });
    setIsCreateOpen(true);
  }

  function openEditForm(role: RoleSummary): void {
    setEditingRole(role);
    editForm.setFieldsValue({
      name: role.name,
      nameEn: role.nameEn,
      description: role.description,
      sortOrder: role.sortOrder,
      isAssignable: role.isAssignable,
      maximumApprovalAmount: role.maximumApprovalAmount,
    });
  }

  /** Mở một hộp con, nạp chi tiết vai trò trước vì danh sách chỉ có phần tóm tắt. */
  async function openDetailModalAsync(
    role: RoleSummary,
    open: (detail: RoleDetail) => void,
  ): Promise<void> {
    try {
      open(await getRole(role.id));
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không nạp được chi tiết vai trò.',
      );
    }
  }

  async function handleCreate(values: CreateFormValues): Promise<void> {
    setIsBusy(true);

    try {
      const created = await createRole(values);

      void message.success('Đã lập vai trò.');
      setIsCreateOpen(false);
      reload();

      // Mở thẳng hộp cấp quyền: vai trò vừa lập chưa có quyền và chưa có quy tắc phạm vi nào, nên nó
      // chưa dùng được. Đưa người quản trị sang bước tiếp theo thay vì để họ tự nhớ.
      setPermissionsRole(created);
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lập được vai trò.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function handleEdit(values: EditFormValues): Promise<void> {
    if (editingRole === null) {
      return;
    }

    setIsBusy(true);

    try {
      await updateRole(editingRole.id, values);

      void message.success('Đã lưu thay đổi.');
      setEditingRole(null);
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không lưu được thay đổi.',
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function handleToggleActive(role: RoleSummary): Promise<void> {
    try {
      await setRoleActive(role.id, !role.isActive);

      /*
        Báo rõ đã xong: khi danh sách đang ẩn vai trò đã ngừng, dòng vừa ngừng biến mất sau lần tải
        lại — không có thông báo thì người dùng không biết đó là đã ngừng thành công hay là lỗi.
      */
      void message.success(
        role.isActive ? `Đã ngừng vai trò ${role.code}.` : `Đã dùng lại vai trò ${role.code}.`,
      );
      reload();
    } catch (error) {
      void message.error(
        error instanceof ApiError ? error.message : 'Không đổi được trạng thái vai trò.',
      );
    }
  }

  const columns: TableColumnsType<RoleSummary> = [
    {
      title: 'Mã',
      dataIndex: 'code',
      width: 230,
      render: (code: string) => <Typography.Text strong>{code}</Typography.Text>,
    },
    { title: 'Tên vai trò', dataIndex: 'name' },
    {
      title: 'Quyền',
      dataIndex: 'permissionCount',
      width: 110,
      render: (count: number) =>
        count === 0 ? (
          <Tooltip title="Vai trò chưa được cấp quyền nào nên chưa làm được gì.">
            <Tag color="orange">Chưa có</Tag>
          </Tooltip>
        ) : (
          <Tag>{count.toLocaleString('vi-VN')} quyền</Tag>
        ),
    },
    {
      title: 'Phạm vi dữ liệu',
      dataIndex: 'dataScopeRuleCount',
      width: 190,
      render: (count: number) =>
        count === 0 ? (
          <Tooltip title="Người giữ vai trò này KHÔNG thấy dòng dữ liệu nào, kể cả khi đã có đủ quyền.">
            <Tag color="red">Chưa khai — không thấy gì</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">{count.toLocaleString('vi-VN')} quy tắc</Tag>
        ),
    },
    {
      title: 'Người giữ',
      dataIndex: 'assignedUserCount',
      width: 110,
      render: (count: number) => count.toLocaleString('vi-VN'),
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 200,
      render: (_value: unknown, role: RoleSummary) => (
        <Space size={4} wrap>
          {role.isActive ? <Tag color="green">Đang dùng</Tag> : <Tag>Đã ngừng</Tag>}

          {role.isSystemRole && (
            <Tooltip title="Quyền được đồng bộ từ mã nguồn ở mỗi lần khởi động, không sửa được.">
              <Tag color="geekblue">Vai trò hệ thống</Tag>
            </Tooltip>
          )}

          {!role.isAssignable && <Tag color="purple">Không gán được</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 330,
      render: (_value: unknown, role: RoleSummary) => (
        <Space size={4} wrap>
          {canViewAssignment && (
            <Button
              size="small"
              icon={<SafetyCertificateOutlined />}
              onClick={() => {
                void openDetailModalAsync(role, setPermissionsRole);
              }}
            >
              Quyền
            </Button>
          )}

          {canViewAssignment && (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => {
                void openDetailModalAsync(role, setDataScopeRole);
              }}
            >
              Phạm vi
            </Button>
          )}

          {/*
            Vai trò hệ thống không sửa được và không ngừng dùng được. Máy chủ đã chặn, nhưng một nút
            chắc chắn thất bại thì không nên hiện ra. Vai trò đã ngừng cũng không hiện nút "Sửa":
            muốn sửa thì dùng lại trước.
          */}
          {canManageRole && !role.isSystemRole && role.isActive && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                openEditForm(role);
              }}
            >
              Sửa
            </Button>
          )}

          {canManageRole && !role.isSystemRole && (
            <Button
              size="small"
              onClick={() => {
                void handleToggleActive(role);
              }}
            >
              {role.isActive ? 'Ngừng' : 'Dùng lại'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Typography.Title level={4} style={{ margin: 0 }}>
        Vai trò và phân quyền
      </Typography.Title>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Alert
        type="info"
        showIcon
        message="Một vai trò dùng được cần cả hai thứ: quyền và phạm vi dữ liệu"
        description={
          'Quyền quyết định người dùng LÀM được những gì; phạm vi dữ liệu quyết định họ THẤY được '
          + 'những dòng nào. Thiếu quyền thì họ không mở được màn hình; thiếu phạm vi thì màn hình '
          + 'mở ra nhưng danh sách rỗng. Cột "Phạm vi dữ liệu" ở dưới báo đỏ khi vai trò còn thiếu.'
        }
      />

      <Space wrap>
        <Input.Search
          placeholder="Tìm theo mã hoặc tên vai trò"
          allowClear
          style={{ width: 300 }}
          onSearch={(value) => {
            setPageNumber(1);
            setSearchTerm(value);
          }}
        />

        <Space size={4}>
          <Switch
            checked={includeInactive}
            onChange={(checked) => {
              setPageNumber(1);
              setIncludeInactive(checked);
            }}
          />
          <Typography.Text>Hiện cả vai trò đã ngừng</Typography.Text>
        </Space>

        <Button icon={<ReloadOutlined />} onClick={reload}>
          Tải lại
        </Button>

        {canManageRole && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateForm}>
            Lập vai trò
          </Button>
        )}
      </Space>

      <Table<RoleSummary>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={[...items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: pageNumber,
          pageSize,
          total: totalCount,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title="Lập vai trò mới"
        open={isCreateOpen}
        confirmLoading={isBusy}
        onOk={() => {
          createForm.submit();
        }}
        onCancel={() => {
          setIsCreateOpen(false);
        }}
        okText="Lập vai trò"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<CreateFormValues>
          form={createForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleCreate(values);
          }}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Vai trò mới chưa dùng được ngay"
            description={
              'Nó chưa có quyền nào và chưa có quy tắc phạm vi nào. Sau khi lưu, hệ thống mở thẳng '
              + 'màn hình cấp quyền — hãy cấp quyền rồi khai phạm vi dữ liệu trước khi gán cho ai.'
            }
          />

          <Form.Item
            name="code"
            label="Mã vai trò"
            extra="Chữ in hoa không dấu, chữ số và gạch dưới. Ví dụ: KINH_DOANH, KE_TOAN. KHÔNG đổi được sau khi lập."
            rules={[{ required: true, message: 'Chưa nhập mã vai trò.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="name"
            label="Tên vai trò"
            rules={[{ required: true, message: 'Chưa nhập tên vai trò.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="nameEn" label="Tên tiếng Anh">
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
            extra="Vai trò này dành cho ai và làm được gì. Đọc lại sau một năm vẫn phải hiểu được."
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự hiển thị"
            extra="Số nhỏ nằm trên. Vai trò hệ thống dùng số nhỏ."
            rules={[{ required: true, message: 'Chưa nhập thứ tự hiển thị.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingRole === null ? 'Sửa vai trò' : `Sửa vai trò ${editingRole.code}`}
        open={editingRole !== null}
        confirmLoading={isBusy}
        onOk={() => {
          editForm.submit();
        }}
        onCancel={() => {
          setEditingRole(null);
        }}
        okText="Lưu"
        cancelText="Hủy"
        destroyOnHidden
      >
        <Form<EditFormValues>
          form={editForm}
          layout="vertical"
          requiredMark={false}
          disabled={isBusy}
          onFinish={(values) => {
            void handleEdit(values);
          }}
        >
          <Form.Item
            name="name"
            label="Tên vai trò"
            rules={[{ required: true, message: 'Chưa nhập tên vai trò.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="nameEn" label="Tên tiếng Anh">
            <Input />
          </Form.Item>

          <Form.Item name="description" label="Mô tả">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="Thứ tự hiển thị"
            rules={[{ required: true, message: 'Chưa nhập thứ tự hiển thị.' }]}
          >
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="isAssignable"
            label="Gán được cho người dùng"
            valuePropName="checked"
            extra="Tắt để giữ vai trò lại mà không cho gán mới. Vai trò dành cho tiến trình tự động nên tắt."
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="maximumApprovalAmount"
            label="Hạn mức duyệt"
            extra="Giá trị chứng từ lớn nhất mà vai trò này duyệt được, tính bằng đồng. Để trống nghĩa là không giới hạn."
          >
            {/*
              Kiểu giá trị khai là số HOẶC chuỗi vì hai hàm dưới đây thật sự làm việc với chuỗi:

              - Hàm định dạng nhận CHUỖI số (ví dụ "1500000"), dù kiểu khai của thư viện ghi là số.
                Gọi toLocaleString trên chuỗi thì trả lại nguyên chuỗi, nên trước đây ô không bao giờ
                có dấu chấm ngăn nghìn. Đổi sang số trước rồi mới định dạng.
              - Hàm đọc phải trả CHUỖI RỖNG khi ô trống. Thư viện hiểu chuỗi rỗng là "không có giá trị"
                và đưa null vào biểu mẫu; trả Number('') thì thành 0 — tức hạn mức duyệt bằng không,
                chặn mọi chứng từ, đúng ngược ý người vừa xóa trắng ô để bỏ giới hạn.

              Giá trị đưa vào biểu mẫu vẫn là số hoặc null: ô không bật chế độ chuỗi.
            */}
            <InputNumber<number | string>
              min={0}
              style={{ width: '100%' }}
              formatter={(value) => {
                const text = value === undefined ? '' : String(value).trim();

                if (text === '') {
                  return '';
                }

                const amount = Number(text);

                return Number.isFinite(amount) ? amount.toLocaleString('vi-VN') : text;
              }}
              parser={(displayValue) => (displayValue ?? '').replace(/[.\s]/gu, '')}
            />
          </Form.Item>
        </Form>
      </Modal>

      <RolePermissionsModal
        role={permissionsRole}
        canManage={canManageAssignment}
        onChanged={(updated) => {
          setPermissionsRole(updated);
          reload();
        }}
        onClose={() => {
          setPermissionsRole(null);
        }}
      />

      <RoleDataScopeModal
        role={dataScopeRole}
        canManage={canManageAssignment}
        onChanged={(updated) => {
          setDataScopeRole(updated);
          reload();
        }}
        onClose={() => {
          setDataScopeRole(null);
        }}
      />
    </div>
  );
}
